// Traverse — Help: which walkthroughs a person has seen (board LHelp "Seen ·
// N steps"; handoff §3.11). Stored per user in users/{uid}.helpSeen[id], set
// when the last step is reached. ASP's Help keeps its own device-only memory;
// this module is the contractor Help's.
//
// firestore mode writes the user's own record (the rule lets a user write
// their own helpSeen: "UI needs · Help" in CONTRACT.md). Fixture mode keeps it
// in this browser.
import { modeFor } from './mode.js';

const LOCAL = 'traverse.fx.helpSeen';
const local = () => { try { return JSON.parse(localStorage.getItem(LOCAL) || '{}'); } catch (_) { return {}; } };
const saveLocal = (v) => { try { localStorage.setItem(LOCAL, JSON.stringify(v)); } catch (_) { /* private window */ } };

/** { [walkthroughId]: true } for this user. */
export async function helpSeen(uid) {
  if (modeFor('help') === 'firestore') {
    const [{ doc, getDoc }, { db }, { ORG_ID }] = await Promise.all([
      import('../fb/firestore.js'), import('../firebase.js'), import('../config.js')]);
    const s = await getDoc(doc(db, `organisations/${ORG_ID}/users/${uid}`)).catch(() => null);
    return (s && s.exists() && s.data().helpSeen) || {};
  }
  return local()[uid] || {};
}

/** Mark one seen. Never unmarks. */
export async function markHelpSeen(uid, id) {
  if (modeFor('help') === 'firestore') {
    const [{ doc, setDoc }, { db }, { ORG_ID }] = await Promise.all([
      import('../fb/firestore.js'), import('../firebase.js'), import('../config.js')]);
    return setDoc(doc(db, `organisations/${ORG_ID}/users/${uid}`), { helpSeen: { [id]: true } }, { merge: true });
  }
  const all = local(); all[uid] = { ...(all[uid] || {}), [id]: true }; saveLocal(all);
  return { ok: true };
}

/**
 * Which walkthroughs this person may see: every `needs` feature on for the
 * company, and their role in `who` (handoff §3.11). Pure.
 */
export function walkthroughsFor(list, { roles = [], featureOn = () => true } = {}) {
  return list.filter((w) => (w.needs || []).every((k) => featureOn(k)) && (!w.who || w.who.some((r) => roles.includes(r))));
}

/** "N steps · 1 min" or "Seen · N steps", from the steps' seconds (board LHelp). */
export function walkthroughMeta(w, seen) {
  const n = (w.steps || []).length;
  if (seen) return { text: `Seen · ${n} steps`, seen: true };
  const secs = (w.steps || []).reduce((a, s) => a + (Number(s.secs) || 6), 0);
  const mins = Math.max(1, Math.round(secs / 60));
  return { text: `${n} steps · ${mins} min`, seen: false };
}

/**
 * "Report a problem" (boards LHelp "Stuck?", MHelp). What the person typed,
 * which screen they were on and which device, nothing else. firestore mode is
 * the `reportProblem` callable ("UI needs · Help" in CONTRACT.md); fixture mode
 * keeps it in this browser.
 */
export async function reportProblem({ text, screen = '', device = '' } = {}) {
  const t = String(text || '').trim().slice(0, 2000);
  if (!t) throw new Error('Say what went wrong.');
  const report = { text: t, screen: String(screen || ''), device: String(device || '') };
  if (modeFor('help') === 'firestore') {
    const { callFunction } = await import('../db.js');
    return callFunction('reportProblem', report);
  }
  const KEY = 'traverse.fx.problems';
  let all = [];
  try { all = JSON.parse(localStorage.getItem(KEY) || '[]'); } catch (_) { all = []; }
  all.push({ ...report, at: new Date().toISOString() });
  try { localStorage.setItem(KEY, JSON.stringify(all)); } catch (_) { /* private window */ }
  return { ok: true, count: all.length };
}

/**
 * What this company has sent with the Issue button or "Stuck?" (23/10/2026):
 * organisations/{org}/problems, newest first. The Owner and Directors may read
 * it (firestore.rules); Track support's progress shows as its state. Read-only.
 * The stand-in shows two sample reports.
 */
export async function listProblems(max = 100) {
  if (globalThis.__TRACK_STAND_IN) {
    const ago = (h) => new Date(Date.now() - h * 3600000).toISOString();
    return [
      { id: 's1', kind: 'bug', text: 'Timesheet total shows 0:00 after saving Friday', screen: '/time', build: 'v4.0.1', byEmail: 'sanj@example.com', at: ago(3), state: 'seen', shotPath: 'x' },
      { id: 's2', kind: 'idea', text: 'Let me pin a project to the top of Projects', detail: 'The three I work on every day.', screen: '/projects', build: 'v4.0.1', byEmail: 'karan@example.com', at: ago(30), state: 'new' },
    ];
  }
  const [{ collection, getDocs, query, orderBy, limit }, { db }, { ORG_ID }] = await Promise.all([
    import('../fb/firestore.js'), import('../firebase.js'), import('../config.js')]);
  const snap = await getDocs(query(collection(db, `organisations/${ORG_ID}/problems`), orderBy('at', 'desc'), limit(max)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
